import * as CheckboxPrimitive from '@radix-ui/react-checkbox'
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group'
import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group'
import type { ReactNode } from 'react'

import { ActiveDot } from '../Card'
import { CheckMark } from './CheckMark'
import { choiceRow, choiceTone } from './Field'

export type Option = {
  value: string
  label: string
  hint?: string
  dot?: string
  disabled?: boolean
}

function RadioMark({ checked }: { checked: boolean }) {
  return (
    <span
      className={`flex h-mark w-mark flex-none items-center justify-center rounded-full border-[1.5px] ${
        checked ? 'border-on-accent bg-on-accent' : 'border-hairline'
      }`}
    >
      {checked ? <span className="h-2 w-2 rounded-full bg-accent" /> : null}
    </span>
  )
}

function CheckMarkBox({ checked, compact = false }: { checked: boolean; compact?: boolean }) {
  return (
    <span
      className={`flex flex-none items-center justify-center border-[1.5px] text-accent ${
        compact
          ? 'h-mark-compact w-mark-compact rounded-mark-compact'
          : 'h-mark w-mark rounded-mark'
      } ${checked ? 'border-on-accent bg-on-accent' : 'border-hairline'}`}
    >
      {checked ? <CheckMark size={compact ? 10 : 12} weight={2.4} /> : null}
    </span>
  )
}

export function RadioRows({
  value,
  onChange,
  options,
  name,
}: {
  value: string
  onChange: (value: string) => void
  options: Option[]
  name?: string
}) {
  return (
    <RadioGroupPrimitive.Root
      value={value}
      onValueChange={onChange}
      name={name}
      className="flex flex-col gap-2"
    >
      {options.map((option) => (
        <RadioGroupPrimitive.Item
          key={option.value}
          value={option.value}
          disabled={option.disabled}
          className={`${choiceRow} ${choiceTone(option.value === value)} disabled:opacity-disabled`}
        >
          <RadioMark checked={option.value === value} />
          {option.dot ? <ActiveDot colour={option.dot} /> : null}
          <span className="flex-1">
            {option.label}
            {option.hint ? <span className="block text-hint opacity-70">{option.hint}</span> : null}
          </span>
        </RadioGroupPrimitive.Item>
      ))}
    </RadioGroupPrimitive.Root>
  )
}

export function CheckboxRows({
  values,
  onChange,
  options,
}: {
  values: string[]
  onChange: (values: string[]) => void
  options: Option[]
}) {
  return (
    <div className="flex flex-col gap-2">
      {options.map((option) => {
        const checked = values.includes(option.value)
        return (
          <CheckboxPrimitive.Root
            key={option.value}
            checked={checked}
            disabled={option.disabled}
            onCheckedChange={() =>
              onChange(
                checked
                  ? values.filter((value) => value !== option.value)
                  : [...values, option.value],
              )
            }
            className={`${choiceRow} ${choiceTone(checked)} disabled:opacity-disabled`}
          >
            <CheckMarkBox checked={checked} />
            {option.dot ? <ActiveDot colour={option.dot} /> : null}
            <span className="flex-1">{option.label}</span>
          </CheckboxPrimitive.Root>
        )
      })}
    </div>
  )
}

export function Chips({
  values,
  onChange,
  options,
  multiple = true,
}: {
  values: string[]
  onChange: (values: string[]) => void
  options: Option[]
  multiple?: boolean
}) {
  return (
    <ToggleGroupPrimitive.Root
      type="multiple"
      value={values}
      onValueChange={(next: string[]) => {
        if (multiple) {
          onChange(next)
          return
        }
        const added = next.find((value) => !values.includes(value))
        onChange(added ? [added] : [])
      }}
      className="flex flex-wrap gap-[7px]"
    >
      {options.map((option) => {
        const checked = values.includes(option.value)
        return (
          <ToggleGroupPrimitive.Item
            key={option.value}
            value={option.value}
            disabled={option.disabled}
            className={`pressable flex min-h-tap items-center gap-2 rounded-chip border px-3 text-hint font-medium transition-colors duration-row disabled:opacity-disabled ${
              checked
                ? 'border-accent bg-accent text-on-accent'
                : 'border-hairline bg-field text-text'
            }`}
          >
            <CheckMarkBox checked={checked} compact />
            {option.dot ? <ActiveDot colour={option.dot} /> : null}
            {option.label}
          </ToggleGroupPrimitive.Item>
        )
      })}
    </ToggleGroupPrimitive.Root>
  )
}

export function Segmented({
  value,
  onChange,
  options,
  ariaLabel,
  tone = 'field',
}: {
  value: string
  onChange: (value: string) => void
  options: { value: string; label: ReactNode }[]
  ariaLabel?: string
  tone?: 'field' | 'quiet'
}) {
  const index = options.findIndex((option) => option.value === value)

  return (
    <div
      className={`relative flex h-field rounded-nested border p-[3px] ${
        tone === 'quiet'
          ? 'border-transparent bg-fill backdrop-blur-2'
          : 'border-segment bg-segment'
      }`}
    >
      {index >= 0 ? (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-y-[3px] rounded-chip bg-segment-thumb shadow-segment transition-[left] duration-thumb ease-sheet"
          style={{
            width: `calc((100% - 6px) / ${options.length})`,
            left: `calc(3px + (100% - 6px) / ${options.length} * ${index})`,
          }}
        />
      ) : null}
      <ToggleGroupPrimitive.Root
        type="single"
        value={value}
        onValueChange={(next: string) => {
          if (next) onChange(next)
        }}
        aria-label={ariaLabel}
        className="relative z-1 flex w-full"
      >
        {options.map((option) => (
          <ToggleGroupPrimitive.Item
            key={option.value}
            value={option.value}
            className={`pressable flex flex-1 items-center justify-center rounded-chip text-list leading-none transition-colors duration-row ${
              option.value === value ? 'font-medium text-text' : 'text-text-secondary'
            }`}
          >
            {option.label}
          </ToggleGroupPrimitive.Item>
        ))}
      </ToggleGroupPrimitive.Root>
    </div>
  )
}
